"use client";

import { InputHTMLAttributes, ReactNode, useId } from "react";

interface BaseInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "size"
> {
  label?: string;
  error?: string;
  helperText?: string;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  onChange?: (value: string) => void;
  value?: string | number;
  size?: "sm" | "md" | "lg";
}

export function BaseInput({
  label,
  error,
  helperText,
  iconBefore,
  iconAfter,
  onChange,
  className = "",
  required,
  size = "md",
  value,
  ...props
}: BaseInputProps) {
  const inputId = useId();

  // Size variants - avoid duplicates by using conditional classes
  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-3 text-[15px]",
  };

  const inputClasses = [
    "w-full rounded-lg border placeholder-tertiary focus:outline-none focus:ring-2 transition-colors",
    sizeClasses[size],
    iconBefore ? "pl-10" : "",
    iconAfter ? "pr-10" : "",
    error
      ? "border-status-error focus:border-status-error focus:ring-status-error"
      : "border-primary focus:border-focus focus:ring-accent-primary",
    "bg-secondary text-primary",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const errorId = error ? `${inputId}-error` : undefined;
  const helperId = !error && helperText ? `${inputId}-helper` : undefined;
  const describedBy = errorId || helperId || undefined;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-primary mb-2"
        >
          {label}
          {required && <span className="text-status-error">*</span>}
        </label>
      )}
      <div className="relative">
        {iconBefore && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            {iconBefore}
          </div>
        )}
        <input
          id={inputId}
          className={inputClasses}
          value={value != null ? String(value) : ""}
          onChange={(e) => {
            try {
              if (!e) {
                return;
              }
              if (!e.target) {
                return;
              }
              if (typeof onChange !== "function") {
                return;
              }
              const inputValue = e.target.value ?? "";
              onChange(inputValue);
            } catch {
            }
          }}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          {...(props as any)}
        />
        {iconAfter && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {iconAfter}
          </div>
        )}
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-sm text-status-error" role="alert">
          {error}
        </p>
      )}
      {!error && helperText && (
        <p id={helperId} className="mt-1 text-sm text-secondary">
          {helperText}
        </p>
      )}
    </div>
  );
}
